// TODO(faq-case-slugs): the knowledge base publishes an empty `slug` on every
// case, so cases have no address of their own. Until that changes:
//   - list keys fall back to the array index (views/faq/{index,section,
//     m_section}.vue) because every case otherwise shares key=""
//   - m_section opens slugless cases in place instead of linking to them
//   - m_case 302s /help/{type}/{section}/{case} to its section
// Grep `faq-case-slugs` for every site to undo, and check the republished slugs
// against the indexed ones — if they differ those URLs want real 301s rather
// than these temporary redirects.

// The knowledge base publishes only a slug and a translated name per category,
// so the artwork for each one is mapped here. `icon` is the illustration on the
// /help landing tiles, `logo` the one on /contact.
const FAQ_CATEGORY_ARTWORK = {
  flight: { icon: 'routes', logo: 'logo_flight' },
  hotel: { icon: 'hotel', logo: 'logo_hotel' }
}

// The tiles require() the icon name, so it has to resolve for every category —
// including ones published after this map was written.
export const FAQ_CATEGORY_FALLBACK_ICON = 'customer_service'

// Categories shown until the published list arrives, and whenever it can't be
// read. Both /help and /contact seed from this so the two pages can't drift.
const FAQ_DEFAULT_CATEGORY_SLUGS = ['flight', 'hotel']

// hasOwnProperty rather than a bare lookup: a slug of `constructor` or
// `toString` would otherwise inherit a truthy value from Object.prototype and
// skip the fallback, leaving require() to throw on a nonexistent asset.
const artworkFor = slug =>
  Object.prototype.hasOwnProperty.call(FAQ_CATEGORY_ARTWORK, slug)
    ? FAQ_CATEGORY_ARTWORK[slug]
    : { icon: FAQ_CATEGORY_FALLBACK_ICON }

// `translate` must be a closure over the component (`key => this.$t(key)`),
// never a bare `this.$t` — vue-i18n 8 defines $t on the prototype and it
// dereferences `this.$i18n`, so an extracted reference throws in data().
export const buildDefaultFaqCategories = translate =>
  FAQ_DEFAULT_CATEGORY_SLUGS.map(slug => ({
    id: slug,
    title: translate(`global.${slug}`),
    ...artworkFor(slug)
  }))

/**
 * v2 answers with `{ categories, groups }`; v1 answered with the group array
 * directly, and the search endpoint still does. Read either shape so one
 * helper serves both and a rollback doesn't blank the page.
 */
export const getFaqGroups = result =>
  Array.isArray(result) ? result : result?.groups || []

/**
 * The section a URL named, or nothing. Matched by slug and nothing else: an
 * unknown `sectionSlug` makes the API answer with every group rather than an
 * error, so anything looser puts an arbitrary section under a stale address —
 * and stale ones are live traffic, still in the Google index.
 *
 * Notably a single-group response is NOT trusted on its own. `account` and
 * `partnership` publish exactly one group each, so trusting it turned every
 * `/help/account/<anything>` into a 200 rendering "General Information". Both
 * versions echo the requested slug on a real match, so an exact test loses
 * nothing.
 *
 * Only call this for a URL that named a section. The category landing asks a
 * different question — see firstFaqSection.
 */
export const findFaqSection = (result, sectionSlug) =>
  getFaqGroups(result)
    .filter(Boolean)
    .find(group => group.slug === sectionSlug)

/**
 * The section the category landing (/help/{type}) opens on. It names no
 * section, so nothing here can be stale — an empty result is a category with
 * nothing published yet, not a wrong URL, and must never be treated as one.
 */
export const firstFaqSection = result => getFaqGroups(result).filter(Boolean)[0]

/**
 * Maps the published categories onto the shape the FAQ views render. Entries
 * missing a slug or a name are dropped — they would render as a blank tile
 * pointing at /help/. Returns an empty array when the response carries none,
 * so the caller can keep the list it already had.
 */
export const mapFaqCategories = result =>
  (result?.categories || [])
    .filter(category => category && category.slug && category.name)
    .map(({ slug, name }) => ({
      id: slug,
      title: name,
      ...artworkFor(slug)
    }))

// Meta title and description for the help pages, keyed by the path under
// /help ('' is the hub). The values are the suffixes Marketing published in
// Weblate as seo.metatitlehelp<suffix> and seo.metadeschelp<suffix>. They do
// not follow from the slugs (special-requests → requests), so every page is
// listed by hand, straight from the Confluence tables. The knowledge base owns
// the slugs, so a page it renames or adds drops out of the map until ids for
// it are published and listed here.
const FAQ_SEO_SUFFIXES = {
  '': '',
  flight: 'flight',
  'flight/frequently-asked-questions': 'flightfaq',
  'flight/general-information': 'flightgeneral',
  'flight/check-in': 'flightcheckin',
  'flight/baggage': 'flightbaggage',
  'flight/cancellation-refund-and-change': 'flightcancel',
  'flight/my-booking': 'flightbooking',
  'flight/special-requests': 'flightrequests',
  hotel: 'hotel',
  'hotel/general-information': 'hotelgeneral',
  'hotel/check-in': 'hotelcheckin',
  'hotel/my-booking': 'hotelbooking',
  'hotel/cancellation-refund-and-change': 'hotelcancel',
  'hotel/review-and-rating': 'hotelreview',
  'hotel/property-owners': 'hotelproperty',
  payment: 'payment',
  'payment/general-information': 'paymentgeneral',
  'payment/invoice-and-receipts': 'paymentinvoice',
  'payment/payment-issues': 'paymentissues',
  'payment/refund': 'paymentrefund',
  'payment/vouchers': 'paymentvoucher',
  account: 'account',
  'account/general-information': 'accountgeneral',
  partnership: 'partnership',
  'partnership/general-information': 'partnershipgeneral',
  affiliator: 'affiliate',
  'affiliator/getting-started': 'affiliatestart',
  'affiliator/account': 'affiliateaccount',
  'affiliator/commissions': 'affiliatecommission',
  'affiliator/withdraw': 'affiliatewithdraw',
  'affiliator/tools': 'affiliatetools'
}

/**
 * The message ids carrying a help page's meta title and description, from
 * its route params — no `type` is the hub, no `section` the category
 * landing. Null for a page none are published for, so the caller can keep
 * its generic head rather than render the missing id as the title.
 */
export const faqSeoMessageIds = ({ type, section } = {}) => {
  const page = [type, section].filter(Boolean).join('/')

  if (!Object.prototype.hasOwnProperty.call(FAQ_SEO_SUFFIXES, page)) return null

  const suffix = FAQ_SEO_SUFFIXES[page]

  return {
    title: `seo.metatitlehelp${suffix}`,
    description: `seo.metadeschelp${suffix}`
  }
}
